<template>
  <div id="sequence_step" class="step_wrapper w-full">
    <StepHeader
      :step="step"
      :can-edit="canEdit"
      :step-metrics="stepMetrics"
      :step-type-text="stepTypeText"
      :total-steps="totalSteps"
      @edit="editStep"
      @move_up="moveStepUp"
      @move_down="moveStepDown"
      @add_above="addStepAbove"
      @add_below="addStepBelow"
      @delete="deleteStep"
    />

    <div
      class="variants_container"
      v-if="
        step?.step_type !== 'PHONE_CALL' &&
        step?.step_type !== 'NEXT_BEST_ACTION' &&
        step?.step_type !== 'MULTI_THREADING' &&
        step?.step_type !== 'VOICE' &&
        step?.step_type !== 'MARKET_RESEARCH' &&
        !(
          step?.automation_type === sequenceAutomationTypes.TASK &&
          ['WHATSAPP_MESSAGE', 'LINKEDIN_INVITE', 'LINKEDIN_INMAIL', 'LINKEDIN_MESSAGE'].includes(step?.step_type)
        )
      "
    >
      <StepVariant
        v-for="(variant, index) in variants"
        :key="variant.id"
        :variant="variant"
        :step="step"
        :variant-length="variants.length"
        :can-edit="canEdit"
        :step-type="step?.step_type"
        :variant-letter="getVariantLetter(index)"
        @update:variant="updateVariantStatus(variant?.id)"
      />
    </div>

    <div
      v-if="canAddMoreVariants && canEdit"
      class="flex flex-row gap-4 items-center cursor-pointer py-3 px-4 ab_button"
      @click="addVariant"
    >
      <ThemeImage
        :icon="imageNames.PLUS"
        alt="add_variant"
        class="size-5 opacity-50"
      />
      <span class="text-sm font-semibold">Add A/B</span>
    </div>


    <StepModals
      v-model:edit-modal="editStepModal"
      v-model:delete-modal="showDeleteSequenceStepModal"
      v-model:move-up-modal="showMoveUpModal"
      v-model:move-down-modal="showMoveDownModal"
      v-model:add-above-modal="showAddAboveModal"
      v-model:add-below-modal="showAddBelowModal"
      :form-data="form_step_data"
      @update:form-data="updateFormData"
      :step="step"
      :sequences="sequences"
      :filters="filters"
      :is-loading="isLoading"
      @update-step="updateStep"
      @delete-step="deleteStepConfirm"
      @move-step-up="moveStepUpConfirm"
      @move-step-down="moveStepDownConfirm"
      @add-step-above="addStepAboveConfirm"
      @add-step-below="addStepBelowConfirm"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";

import StepVariant from "./StepVariant.vue";
import SequenceService from "@/src/api/SequenceService.js";
import { useRoute } from "vue-router";
import { message } from "ant-design-vue";
import StepHeader from "./StepHeader.vue";
import StepModals from "./StepModals.vue";
import {
  sequenceAutomationTypes,
  sequenceStepTypes,
} from "@/src/constants/sequenceConstants.js";
import { stepOptions } from "@/src/constants/sequenceStepOptions.js";

import { imageNames } from "@/src/constants/imageConstants.js";
import { featureNames } from "@/src/constants/globalConstants";

const emit = defineEmits(["update:sequence_step", "add-step-modal"]);
const route = useRoute();
const sequenceId = route.params.id;
const props = defineProps({
  step: { type: Object, required: true },
  canEdit: { type: Boolean, required: true },
  totalSteps: { type: Number, required: false },
  sequences: { type: Array, required: false },
  filters: { type: Object, required: false },
});

const MAX_VARIANTS = 5;
const variants = ref(props.step?.variants || []);
const editStepModal = ref(false);
const showDeleteSequenceStepModal = ref(false);
const showMoveUpModal = ref(false);
const showMoveDownModal = ref(false);
const showAddAboveModal = ref(false);
const showAddBelowModal = ref(false);
const isLoading = ref(false);
const form_step_data = ref({
  automation_type: "",
  step_type: "",
  task_priority: "MEDIUM",
  task_description: "",
  timing_type: "IMMEDIATE",
  delay_time: 30,
  position: 0,
  delay_time_unit: "MINUTES",
  pause_until_complete: true,
  assign_to_owner: false,
});

const updateFormData = (updatedData) => {
  form_step_data.value = { ...updatedData };
};

watch(
  () => props.step,
  (newValue) => {
    form_step_data.value = { ...form_step_data.value, ...newValue };

    if (newValue && "assigned_to_id" in newValue) {
      form_step_data.value.assigned_to_id = newValue.assigned_to_id;
    }
    
    if (newValue && "assign_to_owner" in newValue) {
      form_step_data.value.assign_to_owner = newValue.assign_to_owner;
    }
    
    form_step_data.value.assign_team_member =
      !!form_step_data.value.assigned_to_id;
  },
  { immediate: true },
);

watch(
  () => form_step_data.value.delay_time,
  (newVal) => {
    if (newVal < 0) form_step_data.value.delay_time = 0;
  },
);

const canAddMoreVariants = computed(() => {
  if (
    props.step?.step_type === sequenceStepTypes.PHONE_CALL ||
    props.step?.step_type === sequenceStepTypes.TASK ||
    props.step?.step_type === sequenceStepTypes.MARKET_RESEARCH ||
    props.step?.step_type === sequenceStepTypes.VOICE ||
    props.step?.step_type === sequenceStepTypes.MULTI_THREADING ||
    props.step?.step_type === sequenceStepTypes.NEXT_BEST_ACTION ||
    props.step?.step_type === sequenceStepTypes.QUALIFICATION
  )
    return false;
  if (
    props.step?.automation_type === sequenceAutomationTypes.TASK &&
    [
      sequenceStepTypes.WHATSAPP_MESSAGE,
      sequenceStepTypes.LINKEDIN_INVITE,
      sequenceStepTypes.LINKEDIN_INMAIL,
      sequenceStepTypes.LINKEDIN_MESSAGE,
    ].includes(props.step?.step_type)
  )
    return false;
  return variants.value.length < MAX_VARIANTS - 1;
});

const stepMetrics = computed(() => ({
  total_complete: props.step?.analytics?.total_complete || 0,
  total_active: props.step?.analytics?.total_active || 0,
  total_paused: props.step?.analytics?.total_paused || 0,
  total_replies: props.step?.analytics?.total_replies || 0,
}));

const stepTypeText = computed(() => {
  const automationType = props.step?.automation_type;
  const stepType = props.step?.step_type;
  const category =
    automationType === sequenceAutomationTypes.AI
      ? stepOptions?.ai
      : automationType === sequenceAutomationTypes.AUTOMATED
        ? stepOptions?.automated
        : stepOptions?.manual;
  const option = category?.options?.find(
    (opt) =>
      opt?.stepType === stepType && opt?.automationType === automationType,
  );
  return option?.title || stepType;
});

const getVariantLetter = (index) => String.fromCharCode(65 + index);

const editStep = () => (editStepModal.value = true);

const moveStepUp = () => {
  showMoveUpModal.value = true;
};

const moveStepDown = () => {
  showMoveDownModal.value = true;
};

const addStepAbove = () => {
  showAddAboveModal.value = true;
};

const addStepBelow = () => {
  showAddBelowModal.value = true;
};

const addStepAboveConfirm = () => {
  showAddAboveModal.value = false;
  emit("add-step-modal", props.step?.position);
};

const addStepBelowConfirm = () => {
  showAddBelowModal.value = false;
  emit("add-step-modal", props.step?.position + 1);
};

const moveStepUpConfirm = async () => {
  if (isLoading.value) return;
  
  isLoading.value = true;
  try {
    const response = await SequenceService.moveStepUp(
      sequenceId,
      props.step?.id,
    );
    if (response?.success) {
      message.success("Step moved up successfully");
      emit("update:sequence_step");
    }
  } catch (error) {
    message.error("Failed to move step up");
  } finally {
    isLoading.value = false;
    showMoveUpModal.value = false;
  }
};

const moveStepDownConfirm = async () => {
  if (isLoading.value) return;
  
  isLoading.value = true;
  try {
    const response = await SequenceService.moveStepDown(
      sequenceId,
      props.step?.id,
    );
    if (response?.success) {
      message.success("Step moved down successfully");
      emit("update:sequence_step");
    }
  } catch (error) {
    message.error("Failed to move step down");
  } finally {
    isLoading.value = false;
    showMoveDownModal.value = false;
  }
};

const deleteStep = () => (showDeleteSequenceStepModal.value = true);

const addVariant = async () => {
  if (canAddMoreVariants.value) {
    try {
      const response = await SequenceService.addStepVariant(
        sequenceId,
        props.step?.id,
        {
          subject: "",
          body: "",
        },
      );
      if (response?.success) variants.value.push(response?.variant);
    } catch (error) {
      message.error("Failed to add variant");
    }
  }
};

const updateVariantStatus = (variantId) => {
  emit("update:sequence_step", { variant_id: variantId });
};

const deleteStepConfirm = async () => {
  if (isLoading.value) return;
  
  isLoading.value = true;
  try {
    const response = await SequenceService.deleteSequenceStep(
      props.step?.sequence_id,
      props.step?.id,
    );
    if (response?.success) {
      message.success(`${featureNames.SEQUENCES} step deleted successfully`);
    } else {
      message.error(`Failed to delete ${featureNames.SEQUENCES} step`);
    }
  } catch (error) {
    message.error(`Failed to delete ${featureNames.SEQUENCES} step`);
  } finally {
    isLoading.value = false;
    showDeleteSequenceStepModal.value = false;
    emit("update:sequence_step");
  }
};

const updateStep = async () => {
  try {
    if (
      form_step_data.value?.job_titles ||
      form_step_data.value?.seniorityOptions ||
      form_step_data.value?.departmentOptions
    ) {
      form_step_data.value.metadata = {
        job_titles: form_step_data.value?.job_titles,
        seniority: form_step_data.value?.seniorityOptions,
        departments: form_step_data.value?.departmentOptions,
        sequence_id: form_step_data.value?.sequence_id,
      };
    }

    const stepData = {
      ...form_step_data.value,
      step_id: props.step?.id,
      assign_to_owner: form_step_data.value.automation_type === 'TASK' ? (form_step_data.value.assign_to_owner || false) : false
    };

    if (form_step_data.value.assign_team_member) {
      stepData.assigned_to_id = form_step_data.value.assigned_to_id;
    } else {
      stepData.assigned_to_id = null;
    }
    
    if (stepData.assign_to_owner) {
      stepData.assigned_to_id = null;
    }
    
    if ("assign_team_member" in stepData) {
      delete stepData.assign_team_member;
    }

    const response = await SequenceService.updateSequenceStep(
      props.step?.sequence_id,
      stepData,
    );
    if (response?.success) {
      message.success(`${featureNames.SEQUENCES} step updated successfully`);
      emit("update:sequence_step");
    } else {
      message.error(`Failed to update ${featureNames.SEQUENCES} step`);
    }
  } catch (error) {
    message.error(`Failed to update ${featureNames.SEQUENCES} step`);
  } finally {
    editStepModal.value = false;
  }
};
</script>

<style scoped>
.step_wrapper {
  background-color: var(--white-opacity-2);
  border: 1px solid var(--white-opacity-8);
  border-radius: 10px;
}

.step_separator {
  border-bottom: 1px solid var(--white-opacity-8);
}

.variants_container > *:not(:last-child) {
  border-bottom: 1px solid var(--white-opacity-8);
}

.ab_button {
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
}

.ab_button :hover {
  background-color: var(--white-opacity-2);
}
</style>
